import React, { useState, useEffect } from 'react';
import { Filter } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Season } from '@/types/classTypes';

interface FilterBarProps {
  seasonId: string | null;
  instructorId: string | null; // Keep for backwards compatibility
  visibilityFilter: string;
  onFilterChange: (type: string, value: string | null) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  seasonId,
  instructorId, // Keep for backwards compatibility
  visibilityFilter,
  onFilterChange,
}) => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFilterData = async () => {
      setLoading(true);
      
      try {
        // Fetch seasons
        const { data: seasonsData } = await supabase
          .from('seasons')
          .select('*')
          .order('start_date', { ascending: false });
        
        setSeasons(seasonsData || []);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFilterData();
  }, []);

  const handleReset = () => {
    onFilterChange('seasonId', null);
    onFilterChange('visibility', 'all');
    onFilterChange('instructorId', null); // Keep for backwards compatibility
  };

  return (
    <Card className="bg-white/50 backdrop-blur-sm border border-gray-100/60 shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <Filter className="h-4 w-4 mr-2 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <div className="flex-1">
            <Select
              value={seasonId || ''}
              onValueChange={(value) => onFilterChange('seasonId', value === '' ? null : value)}
              disabled={loading}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="All Seasons" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Seasons</SelectItem>
                {seasons.map((season) => (
                  <SelectItem key={season.id} value={season.id}>
                    {season.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1">
            <Select
              value={visibilityFilter}
              onValueChange={(value) => onFilterChange('visibility', value)}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="All Classes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                <SelectItem value="visible">Visible to Parents</SelectItem>
                <SelectItem value="hidden">Hidden from Parents</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="ml-auto"
          >
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterBar; 